// Mirrors /v1/creative-insights on the API (sbly-dash-collector
// src/sbly-api-server/creativeInsights). Snake_case where the API returns rows
// straight from Snowflake, camelCase where it builds the object itself.

export type Dimension = 'persona' | 'selling_point';
export type VocabStatus = 'proposed' | 'approved' | 'retired';
export type VocabSource = 'ai_draft' | 'ai_suggested' | 'human';
export type JobName = 'observe' | 'draft' | 'map';

export const TAG_FIELD = {
  PERSONA: 'persona',
  LEAD_SELLING_POINT: 'lead_selling_point',
  SUPPORTING_SELLING_POINTS: 'supporting_selling_points',
  HOOK_DEVICE: 'hook_device',
  PERSUASION_ANGLE: 'persuasion_angle',
  FORMAT: 'format',
  PRESENTER: 'presenter',
  OFFER_TYPE: 'offer_type',
  AWARENESS_LEVEL: 'awareness_level',
} as const;
export type TagField = (typeof TAG_FIELD)[keyof typeof TAG_FIELD];

export interface VocabValue {
  value_id: string;
  dimension: Dimension;
  product: string | null;
  value_key: string;
  label: string;
  definition: string | null;
  status: VocabStatus;
  source: VocabSource;
  evidence_count: number | null;
  updated_at: string | null;
}

export interface JobStatus {
  state: string;
  progress: { done: number; total: number } | null;
  result: unknown;
  error: string | null;
  startedAt: number | null;
  finishedAt: number | null;
}

export interface InsightsStatus {
  creatives: number;
  creativesByMedia: Record<string, number>;
  observed: number;
  failed: number;
  costUsd: number | null;
  unpricedCalls: number;
  observeCapPerRun: number;
  jobs: Partial<Record<JobName | 'daily', JobStatus>>;
  products: string[];
}

export interface Observation {
  summary: string | null;
  targetPersona: string;
  personaEvidence: string | null;
  sellingPoints: string[];
  leadSellingPoint: string | null;
}

export interface Tags {
  persona: string | null;
  lead_selling_point: string | null;
  supporting_selling_points: string[];
  hook_device: string | null;
  persuasion_angle: string | null;
  format: string | null;
  presenter: string | null;
  offer_type: string | null;
  awareness_level: string | null;
}

export interface TaggedCreative {
  creative_key: string;
  ad_id: string | null;
  media_type: 'video' | 'static' | 'carousel';
  product: string;
  spend: number | null;
  status: 'done' | 'failed';
  error_message: string | null;
  observation: Observation | null;
  tags: Tags;
  overridden: TagField[];
  unmatched_persona: string | null;
  unmatched_selling_point: string | null;
  mapped: boolean;
  thumb_proxy_url: string | null;
  image_proxy_url: string | null;
  is_video: boolean;
}

// The enum dimensions and their allowed values, as defined in the collector's
// Creative Intelligence taxonomy (db/enums.js FEATURE_DEFINITIONS and
// statics/staticTaxonomy.js). Used for the correction dropdowns; the API
// re-validates every correction against the same lists.
export const ENUM_OPTIONS: Record<string, string[]> = {
  hook_device: [
    'question',
    'bold_claim',
    'problem_callout',
    'curiosity_gap',
    'pattern_interrupt',
    'contrarian',
    'audience_callout',
    'other',
  ],
  persuasion_angle: [
    'pain_relief',
    'aspiration',
    'mechanism',
    'myth_busting',
    'social_proof',
    'authority',
    'novelty',
    'other',
  ],
  presenter: [
    'founder',
    'customer',
    'influencer',
    'actor_spokesperson',
    'expert',
    'voiceover_only',
    'none',
    'other',
  ],
  offer_type: [
    'discount',
    'bundle',
    'free_trial',
    'guarantee',
    'gift_bonus',
    'limited_time',
    'none',
    'other',
  ],
  awareness_level: [
    'unaware',
    'problem_aware',
    'solution_aware',
    'product_aware',
  ],
};
export const VIDEO_FORMATS = [
  'talking_head',
  'ugc_selfie',
  'voiceover_broll',
  'screen_recording',
  'slideshow_text',
  'animation',
  'other',
];
export const STATIC_FORMATS = [
  'studio',
  'ugc',
  'text_poster',
  'screenshot',
  'before_after',
  'meme',
  'illustration',
  'collage',
  'other',
];

export const FIELD_LABELS: Record<TagField, string> = {
  persona: 'Persona',
  lead_selling_point: 'Lead selling point',
  supporting_selling_points: 'Supporting selling points',
  hook_device: 'Hook',
  persuasion_angle: 'Angle',
  format: 'Format',
  presenter: 'Presenter',
  offer_type: 'Offer',
  awareness_level: 'Awareness',
};

/** 'problem_callout' → 'Problem callout' */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}
